import React, { useState, useCallback } from 'react';
import { 
  DocumentArrowDownIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
  UserGroupIcon,
  ShoppingBagIcon,
  ClockIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';

const Reports = ({ user }) => {
  const [loading, setLoading] = useState(false);
  const [reportType, setReportType] = useState('leads');
  const [dateRange, setDateRange] = useState('all');
  const [customDateRange, setCustomDateRange] = useState({
    startDate: '',
    endDate: ''
  });
  const [specificDate, setSpecificDate] = useState('');
  const [specificMonth, setSpecificMonth] = useState('');
  const [specificYear, setSpecificYear] = useState('');
  const [rateRange, setRateRange] = useState({
    min: '',
    max: ''
  });
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');

  // Reset specific date filters when date range type changes
  const handleDateRangeChange = (newDateRange) => {
    setDateRange(newDateRange);
    // Reset specific date filters when changing date range type
    if (newDateRange !== 'specific_date') setSpecificDate('');
    if (newDateRange !== 'specific_month') setSpecificMonth('');
    if (newDateRange !== 'specific_year') setSpecificYear('');
    if (newDateRange !== 'custom') {
      setCustomDateRange({ startDate: '', endDate: '' });
    }
  };

  // Generate year options for dropdown
  const getYearOptions = () => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 10 }, (_, i) => currentYear - i);
  };

  // Report type options
  const reportTypes = [
    { id: 'leads', name: 'Leads Report', icon: UserGroupIcon, description: 'All leads with details' },
    { id: 'orders', name: 'Orders Report', icon: ShoppingBagIcon, description: 'Order details and revenue' },
    { id: 'followups', name: 'Follow-ups Report', icon: ClockIcon, description: 'Scheduled follow-ups' },
    { id: 'conversions', name: 'Conversion Report', icon: ChartBarIcon, description: 'Lead conversion analytics' },
    { id: 'revenue', name: 'Revenue Report', icon: CurrencyDollarIcon, description: 'Revenue by period and source' },
    { id: 'overdue', name: 'Overdue Report', icon: ExclamationTriangleIcon, description: 'Overdue follow-ups and tasks' }
  ];

  // Get date range for queries
  const getDateRange = useCallback(() => {
    if (dateRange === 'custom') {
      return {
        start: customDateRange.startDate ? new Date(customDateRange.startDate) : null,
        end: customDateRange.endDate ? new Date(customDateRange.endDate) : null
      };
    }
    
    if (dateRange === 'specific_date') {
      if (!specificDate) return { start: null, end: null };
      const selectedDate = new Date(specificDate);
      const startOfDay = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());
      const endOfDay = new Date(startOfDay.getTime() + 24 * 60 * 60 * 1000);
      return { start: startOfDay, end: endOfDay };
    }
    
    if (dateRange === 'specific_month') {
      if (!specificMonth) return { start: null, end: null };
      const [year, month] = specificMonth.split('-');
      const startOfMonth = new Date(parseInt(year), parseInt(month) - 1, 1);
      const endOfMonth = new Date(parseInt(year), parseInt(month), 1);
      return { start: startOfMonth, end: endOfMonth };
    }
    
    if (dateRange === 'specific_year') {
      if (!specificYear) return { start: null, end: null };
      const startOfYear = new Date(parseInt(specificYear), 0, 1);
      const endOfYear = new Date(parseInt(specificYear) + 1, 0, 1);
      return { start: startOfYear, end: endOfYear };
    }
    
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    switch (dateRange) {
      case 'today':
        return { start: today, end: new Date(today.getTime() + 24 * 60 * 60 * 1000) };
      case 'week':
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay());
        return { start: startOfWeek, end: now };
      case 'month':
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        return { start: startOfMonth, end: now };
      case 'quarter':
        const quarter = Math.floor(now.getMonth() / 3);
        const startOfQuarter = new Date(now.getFullYear(), quarter * 3, 1);
        return { start: startOfQuarter, end: now };
      case 'year':
        const startOfYear = new Date(now.getFullYear(), 0, 1);
        return { start: startOfYear, end: now };
      default:
        return { start: null, end: null };
    }
  }, [dateRange, customDateRange, specificDate, specificMonth, specificYear]);

  // Fetch leads data
  const fetchLeadsData = useCallback(async () => {
    const { start, end } = getDateRange();
    let query = supabase
      .from('leads')
      .select('*')
      .or(`assigned_to.eq.${user.id},user_id.eq.${user.id}`);

    if (start && end) {
      query = query.gte('created_at', start.toISOString()).lte('created_at', end.toISOString());
    }

    if (statusFilter !== 'all') {
      query = query.eq('status', statusFilter);
    }

    if (priorityFilter !== 'all') {
      query = query.eq('priority', priorityFilter);
    }

    if (sourceFilter !== 'all') {
      query = query.eq('source', sourceFilter);
    }

    const { data, error } = await query.order('created_at', { ascending: false });
    if (error) throw error;
    return data || [];
  }, [user.id, getDateRange, statusFilter, priorityFilter, sourceFilter]);

  // Fetch orders data
  const fetchOrdersData = useCallback(async () => {
    const { start, end } = getDateRange();
    let query = supabase
      .from('orders')
      .select('*')
      .eq('user_id', user.id);

    if (start && end) {
      query = query.gte('conversion_date', start.toISOString()).lte('conversion_date', end.toISOString());
    }

    if (statusFilter !== 'all') {
      query = query.eq('status', statusFilter);
    }

    // Apply rate range filter
    if (rateRange.min) {
      query = query.gte('rate', parseFloat(rateRange.min));
    }
    if (rateRange.max) {
      query = query.lte('rate', parseFloat(rateRange.max));
    }

    const { data: ordersData, error: ordersError } = await query.order('conversion_date', { ascending: false });
    if (ordersError) throw ordersError;

    // If we have orders, fetch the corresponding leads
    if (ordersData && ordersData.length > 0) {
      const leadIds = ordersData.map(order => order.lead_id).filter(Boolean);
      
      if (leadIds.length > 0) {
        const { data: leadsData, error: leadsError } = await supabase
          .from('leads')
          .select('id, name, phone, source')
          .in('id', leadIds);
        
        if (leadsError) {
          console.error('Error fetching leads for orders:', leadsError);
          // Continue with orders even if leads fetch fails
        }
        
        // Combine orders with lead data
        const ordersWithLeads = ordersData.map(order => {
          const lead = leadsData?.find(l => l.id === order.lead_id);
          return {
            ...order,
            leads: lead || { id: null, name: 'Unknown', phone: 'Unknown', source: 'Unknown' }
          };
        });
        
        return ordersWithLeads;
      }
    }
    
    return ordersData || [];
  }, [user.id, getDateRange, statusFilter, rateRange]);

  // Fetch follow-ups data
  const fetchFollowUpsData = useCallback(async () => {
    const { start, end } = getDateRange();
    let query = supabase
      .from('leads')
      .select('*')
      .or(`assigned_to.eq.${user.id},user_id.eq.${user.id}`)
      .not('follow_up_date', 'is', null);

    if (start && end) {
      query = query.gte('follow_up_date', start.toISOString()).lte('follow_up_date', end.toISOString());
    }

    if (statusFilter !== 'all') {
      query = query.eq('status', statusFilter);
    }

    if (priorityFilter !== 'all') {
      query = query.eq('priority', priorityFilter);
    }

    const { data, error } = await query.order('follow_up_date', { ascending: true });
    if (error) throw error;
    return data || [];
  }, [user.id, getDateRange, statusFilter, priorityFilter]);

  // Generate Excel file
  const generateExcel = useCallback((data, filename) => {
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Report');
    XLSX.writeFile(wb, `${filename}.xlsx`);
  }, []);

  // Process data for Excel export
  const processDataForExport = useCallback((data, type) => {
    switch (type) {
      case 'leads':
        return data.map(lead => ({
          'Lead ID': lead.id,
          'Name': lead.name,
          'Phone': lead.phone,
          'Alternative Mobile': lead.alternative_mobile || '',
          'Email': lead.email || '',
          'Source': lead.source,
          'Status': lead.status,
          'Priority': lead.priority || 'low',
          'Age': lead.age || '',
          'Address': lead.address || '',
          'City': lead.city || '',
          'State': lead.state || '',
          'Pincode': lead.pincode || '',
          'Last Action': lead.last_action || '',
          'Last Action Date': lead.last_action_date ? new Date(lead.last_action_date).toLocaleDateString() : '',
          'Follow-up Date': lead.follow_up_date ? new Date(lead.follow_up_date).toLocaleDateString() : '',
          'Created Date': new Date(lead.created_at).toLocaleDateString(),
          'Notes': lead.notes || ''
        }));

      case 'orders':
        return data.map(order => ({
          'Order ID': order.id,
          'Lead ID': order.lead_id,
          'Customer Name': order.leads?.name || 'Unknown',
          'Customer Phone': order.leads?.phone || 'Unknown',
          'Customer Source': order.leads?.source || 'Unknown',
          'Quantity': order.quantity || 0,
          'Rate': order.rate || 0,
          'Total Value': (order.quantity || 0) * (order.rate || 0),
          'Status': order.status,
          'Address Line 1': order.address_line_1 || '',
          'Address Line 2': order.address_line_2 || '',
          'City': order.city || '',
          'State': order.state || '',
          'Pin Code': order.pin || '',
          'Conversion Date': new Date(order.conversion_date).toLocaleDateString(),
          'Created Date': new Date(order.created_at).toLocaleDateString()
        }));

      case 'followups':
        return data.map(lead => ({
          'Lead ID': lead.id,
          'Name': lead.name,
          'Phone': lead.phone,
          'Source': lead.source,
          'Status': lead.status,
          'Priority': lead.priority || 'low',
          'Last Action': lead.last_action || '',
          'Follow-up Date': new Date(lead.follow_up_date).toLocaleDateString(),
          'Follow-up Time': new Date(lead.follow_up_date).toLocaleTimeString(),
          'Is Overdue': new Date(lead.follow_up_date) < new Date() ? 'Yes' : 'No',
          'Created Date': new Date(lead.created_at).toLocaleDateString()
        }));

      case 'conversions':
        const conversionData = data.map(lead => ({
          'Lead ID': lead.id,
          'Name': lead.name,
          'Phone': lead.phone,
          'Source': lead.source,
          'Status': lead.status,
          'Created Date': new Date(lead.created_at).toLocaleDateString(),
          'Converted Date': lead.status === 'converted' ? new Date(lead.last_action_date).toLocaleDateString() : '',
          'Days to Convert': lead.status === 'converted' && lead.last_action_date ? 
            Math.ceil((new Date(lead.last_action_date) - new Date(lead.created_at)) / (1000 * 60 * 60 * 24)) : ''
        }));
        return conversionData;

      case 'revenue':
        return data.map(order => ({
          'Order ID': order.id,
          'Customer Name': order.leads?.name || 'Unknown',
          'Source': order.leads?.source || 'Unknown',
          'Quantity': order.quantity || 0,
          'Rate': order.rate || 0,
          'Total Revenue': (order.quantity || 0) * (order.rate || 0),
          'Status': order.status,
          'Conversion Date': new Date(order.conversion_date).toLocaleDateString()
        }));

      case 'overdue':
        return data.filter(lead => new Date(lead.follow_up_date) < new Date()).map(lead => ({
          'Lead ID': lead.id,
          'Name': lead.name,
          'Phone': lead.phone,
          'Source': lead.source,
          'Priority': lead.priority || 'low',
          'Follow-up Date': new Date(lead.follow_up_date).toLocaleDateString(),
          'Days Overdue': Math.ceil((new Date() - new Date(lead.follow_up_date)) / (1000 * 60 * 60 * 24)),
          'Last Action': lead.last_action || '',
          'Status': lead.status
        }));

      default:
        return data;
    }
  }, []);


  // Generate report
  const generateReport = async () => {
    try {
      setLoading(true);
      let data = [];
      let filename = '';

      switch (reportType) {
        case 'leads':
          data = await fetchLeadsData();
          filename = `Leads_Report_${new Date().toISOString().split('T')[0]}`;
          break;
        case 'orders':
          data = await fetchOrdersData();
          filename = `Orders_Report_${new Date().toISOString().split('T')[0]}`;
          break;
        case 'followups':
          data = await fetchFollowUpsData();
          filename = `Followups_Report_${new Date().toISOString().split('T')[0]}`;
          break;
        case 'conversions':
          data = await fetchLeadsData();
          filename = `Conversions_Report_${new Date().toISOString().split('T')[0]}`;
          break;
        case 'revenue':
          data = await fetchOrdersData();
          filename = `Revenue_Report_${new Date().toISOString().split('T')[0]}`;
          break;
        case 'overdue':
          data = await fetchFollowUpsData();
          filename = `Overdue_Report_${new Date().toISOString().split('T')[0]}`;
          break;
        default:
          throw new Error('Invalid report type');
      }

      if (data.length === 0) {
        toast.error('No data found for the selected criteria');
        return;
      }

      const processedData = processDataForExport(data, reportType);
      generateExcel(processedData, filename);
      toast.success(`Report generated successfully! ${data.length} records exported.`);
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error(`Error generating report: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Reports</h1>
        <p className="text-gray-600">Generate comprehensive reports and export to Excel. Select filters below to customize your report.</p>
      </div>

      {/* Report Type Selection */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Select Report Type</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {reportTypes.map((type) => {
            const IconComponent = type.icon;
            return (
              <button
                key={type.id}
                onClick={() => setReportType(type.id)}
                className={`p-4 rounded-lg border-2 text-left transition-colors ${
                  reportType === type.id
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center mb-2">
                  <IconComponent className="h-6 w-6 text-gray-600 mr-3" />
                  <span className="font-medium text-gray-900">{type.name}</span>
                </div>
                <p className="text-sm text-gray-500">{type.description}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Report Filters</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Date Range */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date Range
              </label>
              <select
                value={dateRange}
                onChange={(e) => handleDateRangeChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
                <option value="quarter">This Quarter</option>
                <option value="year">This Year</option>
                <option value="specific_date">Specific Date</option>
                <option value="specific_month">Specific Month</option>
                <option value="specific_year">Specific Year</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>

            {/* Custom Date Range */}
            {dateRange === 'custom' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={customDateRange.startDate}
                    onChange={(e) => setCustomDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={customDateRange.endDate}
                    onChange={(e) => setCustomDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </>
            )}

            {/* Specific Date */}
            {dateRange === 'specific_date' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select Date
                </label>
                <input
                  type="date"
                  value={specificDate}
                  onChange={(e) => setSpecificDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            )}

            {/* Specific Month */}
            {dateRange === 'specific_month' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select Month
                </label>
                <input
                  type="month"
                  value={specificMonth}
                  onChange={(e) => setSpecificMonth(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            )}

            {/* Specific Year */}
            {dateRange === 'specific_year' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select Year
                </label>
                <select
                  value={specificYear}
                  onChange={(e) => setSpecificYear(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select Year</option>
                  {getYearOptions().map(year => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Status</option>
                <option value="new">New</option>
                <option value="follow_up">Follow Up</option>
                <option value="converted">Converted</option>
                <option value="closed">Closed</option>
              </select>
            </div>

            {/* Priority Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Priority
              </label>
              <select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Priorities</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>

            {/* Source Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Source
              </label>
              <select
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Sources</option>
                <option value="Website">Website</option>
                <option value="Referral">Referral</option>
                <option value="Cold Call">Cold Call</option>
                <option value="WhatsApp Lead">WhatsApp Lead</option>
                <option value="Social Media">Social Media</option>
              </select>
            </div>

            {/* Rate Range (for orders) */}
            {reportType === 'orders' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Min Rate (₹)
                  </label>
                  <input
                    type="number"
                    value={rateRange.min}
                    onChange={(e) => setRateRange(prev => ({ ...prev, min: e.target.value }))}
                    placeholder="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Max Rate (₹)
                  </label>
                  <input
                    type="number"
                    value={rateRange.max}
                    onChange={(e) => setRateRange(prev => ({ ...prev, max: e.target.value }))}
                    placeholder="No limit"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </>
            )}
          </div>
        </div>

      {/* Generate Report Button */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Generate Report</h3>
            <p className="text-sm text-gray-500">
              Export {reportTypes.find(t => t.id === reportType)?.name} to Excel format
            </p>
          </div>
          <button
            onClick={generateReport}
            disabled={loading}
            className="flex items-center px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Generating...
              </>
            ) : (
              <>
                <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
                Generate Excel Report
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Reports;
